import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { AlertCircle, Download, ExternalLink, Eye, Info, Loader2, PauseCircle, Search, Users } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  DespachoPageShell,
  DespachoSectionCard,
  DespachoSimpleTable,
} from "@/components/despacho/DespachoUi";
import { buildClientesDeepLink } from "@/lib/clientesDeepLink";
import {
  EMPTY_FISCAL_BOARD_MESSAGE,
  FORBIDDEN_FISCAL_BOARD_MESSAGE,
  LOAD_FISCAL_BOARD_ERROR,
  fiscalAssignmentBoardApi,
  parseFiscalAssignmentBoardError,
  parseFiscalAssignmentSaveError,
} from "@/modules/despacho/despachoFiscalAssignmentApi";
import {
  DESPACHO_NO_ASSIGNED_CLIENTS_MESSAGE,
  DESPACHO_NO_STAFF_FOR_ASSIGNMENT_MESSAGE,
  activeStaffForAssignment,
  assignedUserIds,
  buildAssignmentsPayload,
  formatAssignedUsersLabel,
  isDespachoAssignmentScopedUser,
} from "@/modules/despacho/despachoAssignmentsHelpers";
import { DESPACHO_ASSIGNMENTS_FORBIDDEN_MESSAGE } from "@/modules/despacho/despachoPermissions";
import { ensureFiscalCurrentMonthSynced } from "@/modules/despacho/despachoFiscalAutoSync";
import {
  countFiscalBoardByView,
  filterFiscalBoardItems,
  fiscalBoardRowsEligibleForBulk,
  buildFiscalBoardStaffSummary,
  isFiscalBoardStaffSummaryView,
  isFiscalBoardStaffFilterAll,
  resolveFiscalBoardBulkStaffId,
  resolveFiscalBoardExportStaffId,
  filterRowsAssignedToStaff,
  mergeFiscalBoardAssignmentUpdates,
  FISCAL_BOARD_STAFF_FILTER_ALL,
  FISCAL_BOARD_VIEW_TODAS,
  FISCAL_BOARD_VIEW_POR_AUXILIAR,
  FISCAL_BOARD_VIEW_SIN_ASIGNAR,
  FISCAL_BOARD_VIEW_SUSPENDIDAS,
  FISCAL_BOARD_VIEW_ACTIVAS,
} from "@/modules/despacho/despachoFiscalBoardHelpers";
import {
  fiscalContactSummaryLabel,
  fiscalContactSummaryTone,
  tipoPersonaFiscalLabel,
} from "@/modules/despacho/despachoFiscalContactHelpers";
import { isClientOperativoSuspendido } from "@/lib/clientOperativoStatus";
import { despachoStaffApi } from "@/modules/despacho/despachoStaffApi";
import { despachoRoleLabel } from "@/modules/despacho/despachoStaffHelpers";
import { useAuth } from "@/lib/auth";
import { useCapabilities } from "@/lib/useCapabilities";
import {
  DESPACHO_READ_ONLY_BADGE,
  DESPACHO_READ_ONLY_HELP,
  canManageDespachoAssignments,
  isDespachoReadOnly,
} from "@/modules/despacho/despachoPermissions";

function ContactReadinessBadge({ readiness }) {
  const tone = fiscalContactSummaryTone(readiness);
  const cls =
    tone === "green"
      ? "bg-emerald-50 text-emerald-900 border-emerald-200"
      : tone === "amber"
        ? "bg-amber-50 text-amber-950 border-amber-200"
        : tone === "rose"
          ? "bg-rose-50 text-rose-900 border-rose-200"
          : "bg-slate-50 text-slate-700 border-slate-200";
  return (
    <span className={`inline-block text-[10px] font-medium px-2 py-0.5 rounded-sm border ${cls}`}>
      {fiscalContactSummaryLabel(readiness)}
    </span>
  );
}

function OperativoStatusBadge({ row }) {
  if (!isClientOperativoSuspendido(row)) return null;
  return (
    <Badge
      variant="secondary"
      className="rounded-sm bg-amber-50 text-amber-950 border-amber-200 text-[10px] font-medium"
      data-testid={`despacho-client-suspended-badge-${row.fiscal_client_id}`}
    >
      <PauseCircle className="h-3 w-3 mr-1" aria-hidden />
      Suspendida
    </Badge>
  );
}

const BULK_STAFF_SESSION_KEY = "vf:despacho_bulk_staff_id";

function persistBulkStaffId(staffId) {
  try {
    if (!staffId || staffId === FISCAL_BOARD_STAFF_FILTER_ALL) {
      window.sessionStorage.removeItem(BULK_STAFF_SESSION_KEY);
      return;
    }
    window.sessionStorage.setItem(BULK_STAFF_SESSION_KEY, staffId);
  } catch {
    /* ignore */
  }
}

export default function ClientesDespacho() {
  const { user } = useAuth();
  const { permissions, role, isPlatformDesigner, loading: capabilitiesLoading } = useCapabilities();
  const permCtx = { permissions, role, isPlatformDesigner, capabilitiesLoading, user };
  const canManageAssignments = canManageDespachoAssignments(permCtx);
  const readOnly = isDespachoReadOnly(permCtx);
  const scopedStaffUser = isDespachoAssignmentScopedUser(user);

  const [items, setItems] = useState([]);
  const [loadState, setLoadState] = useState("loading");
  const [loadMessage, setLoadMessage] = useState("");

  const [assignOpen, setAssignOpen] = useState(false);
  const [assignClient, setAssignClient] = useState(null);
  const [staffOptions, setStaffOptions] = useState([]);
  const [selectedStaffId, setSelectedStaffId] = useState("");
  const [assignError, setAssignError] = useState("");
  const [assignSaving, setAssignSaving] = useState(false);
  const [staffLoading, setStaffLoading] = useState(false);

  const [bulkStaffId, setBulkStaffId] = useState(
    scopedStaffUser ? "" : FISCAL_BOARD_STAFF_FILTER_ALL,
  );
  const [bulkSaving, setBulkSaving] = useState(false);
  const [rowSavingId, setRowSavingId] = useState("");

  const [boardView, setBoardView] = useState(
    scopedStaffUser ? FISCAL_BOARD_VIEW_ACTIVAS : FISCAL_BOARD_VIEW_SIN_ASIGNAR,
  );
  const [searchQuery, setSearchQuery] = useState("");
  const [filterStaffId, setFilterStaffId] = useState(
    scopedStaffUser ? "" : FISCAL_BOARD_STAFF_FILTER_ALL,
  );
  const [exportBusy, setExportBusy] = useState("");

  const loadStaffOptions = useCallback(async () => {
    setStaffLoading(true);
    try {
      const staff = await despachoStaffApi.list();
      const active = activeStaffForAssignment(staff);
      setStaffOptions(active);
      setBulkStaffId((prev) => {
        if (prev && !isFiscalBoardStaffFilterAll(prev) && active.some((s) => s.id === prev)) {
          return prev;
        }
        return FISCAL_BOARD_STAFF_FILTER_ALL;
      });
    } catch {
      setStaffOptions([]);
      setBulkStaffId("");
    } finally {
      setStaffLoading(false);
    }
  }, []);

  const loadBoard = useCallback(async ({ silent = false } = {}) => {
    if (!silent) {
      setLoadState("loading");
      setLoadMessage("");
    }
    try {
      const rows = await fiscalAssignmentBoardApi.listBoard();
      setItems(rows);
      if (!silent) {
        setLoadState(rows.length ? "ready" : "empty");
      }
      return rows;
    } catch (err) {
      const parsed = parseFiscalAssignmentBoardError(err);
      if (!silent) {
        setItems([]);
        setLoadState(parsed.kind === "forbidden" ? "forbidden" : "error");
        setLoadMessage(parsed.message);
      }
      throw err;
    }
  }, []);

  useEffect(() => {
    loadBoard();
  }, [loadBoard]);

  useEffect(() => {
    if (!scopedStaffUser) return;
    ensureFiscalCurrentMonthSynced().catch(() => {});
  }, [scopedStaffUser]);

  useEffect(() => {
    if (readOnly) return;
    if (canManageAssignments || boardView === FISCAL_BOARD_VIEW_POR_AUXILIAR) {
      loadStaffOptions();
    }
  }, [canManageAssignments, readOnly, loadStaffOptions, boardView]);

  const openAssign = async (row) => {
    if (!canManageAssignments || !row?.fiscal_client_id) return;
    setAssignClient(row);
    setSelectedStaffId(assignedUserIds(row.assigned_users)[0] || "");
    setAssignError("");
    setAssignOpen(true);
    if (staffOptions.length === 0) {
      setStaffLoading(true);
      try {
        const staff = await despachoStaffApi.list();
        setStaffOptions(activeStaffForAssignment(staff));
      } catch {
        setStaffOptions([]);
        setAssignError("No pudimos cargar usuarios internos del despacho.");
      } finally {
        setStaffLoading(false);
      }
    }
  };

  const selectStaffForAssign = (staffId) => {
    setSelectedStaffId((prev) => (prev === staffId ? "" : staffId));
  };

  const handleSaveAssignments = async () => {
    if (!assignClient?.fiscal_client_id) return;
    setAssignSaving(true);
    setAssignError("");
    try {
      const saved = await fiscalAssignmentBoardApi.replaceAssignments(
        assignClient.fiscal_client_id,
        buildAssignmentsPayload(selectedStaffId ? [selectedStaffId] : []),
      );
      setItems((prev) =>
        mergeFiscalBoardAssignmentUpdates(prev, [
          {
            fiscal_client_id: assignClient.fiscal_client_id,
            assigned_users: saved?.assigned_users ?? [],
          },
        ]),
      );
      toast.success("Auxiliar responsable actualizado.");
      setAssignOpen(false);
      loadBoard({ silent: true }).catch(() => {});
    } catch (err) {
      const parsed = parseFiscalAssignmentSaveError(err);
      setAssignError(
        parsed.kind === "assignments_forbidden"
          ? DESPACHO_ASSIGNMENTS_FORBIDDEN_MESSAGE
          : parsed.message,
      );
    } finally {
      setAssignSaving(false);
    }
  };

  const bulkTargetStaffId = useMemo(
    () => resolveFiscalBoardBulkStaffId({ boardView, filterStaffId, bulkStaffId }),
    [boardView, filterStaffId, bulkStaffId],
  );

  const applyBulkAssignment = async ({
    fiscalClientIds,
    action,
    successMessage,
    staffUserId,
    trackBulkSaving = true,
  }) => {
    const targetStaffId = staffUserId || bulkTargetStaffId;
    if (!targetStaffId || !fiscalClientIds.length) return;
    if (trackBulkSaving) setBulkSaving(true);
    try {
      const data = await fiscalAssignmentBoardApi.bulkAssignments({
        staffUserId: targetStaffId,
        fiscalClientIds,
        action,
      });
      const updates = Array.isArray(data?.updated) ? data.updated : [];
      setItems((prev) => mergeFiscalBoardAssignmentUpdates(prev, updates));
      toast.success(successMessage);
      loadBoard({ silent: true }).catch(() => {});
    } catch (err) {
      const parsed = parseFiscalAssignmentSaveError(err);
      toast.error(
        parsed.kind === "assignments_forbidden"
          ? DESPACHO_ASSIGNMENTS_FORBIDDEN_MESSAGE
          : parsed.message,
      );
    } finally {
      if (trackBulkSaving) setBulkSaving(false);
      setRowSavingId("");
    }
  };

  const toggleRowBulkAssignment = async (row, nextChecked, staffUserIdOverride) => {
    const rowAssignedIds = assignedUserIds(row?.assigned_users);
    let targetStaffId = staffUserIdOverride || bulkTargetStaffId;
    if (!targetStaffId && nextChecked === false && rowAssignedIds.length > 0) {
      targetStaffId = rowAssignedIds[0];
    }
    if (!canManageAssignments || !targetStaffId || !row?.fiscal_client_id) return;
    if (isClientOperativoSuspendido(row)) return;
    if (rowSavingId === row.fiscal_client_id) return;
    const assigned = assignedUserIds(row?.assigned_users).includes(targetStaffId);
    const shouldAssign = typeof nextChecked === "boolean" ? nextChecked : !assigned;
    if (shouldAssign === assigned) return;
    setRowSavingId(row.fiscal_client_id);
    await applyBulkAssignment({
      fiscalClientIds: [row.fiscal_client_id],
      action: shouldAssign ? "assign" : "unassign",
      successMessage: shouldAssign ? "Auxiliar asignado." : "Asignación quitada.",
      staffUserId: targetStaffId,
      trackBulkSaving: false,
    });
  };

  const handleSelectAllBulk = async () => {
    const ids = fiscalBoardRowsEligibleForBulk(visibleRows).map((row) => row.fiscal_client_id);
    if (!ids.length || !bulkTargetStaffId) return;
    await applyBulkAssignment({
      fiscalClientIds: ids,
      action: "assign",
      successMessage: "Auxiliar asignado a todas las empresas visibles.",
    });
  };

  const handleClearAllBulk = async () => {
    const ids = filterRowsAssignedToStaff(
      fiscalBoardRowsEligibleForBulk(visibleRows),
      bulkTargetStaffId,
    ).map((row) => row.fiscal_client_id);
    if (!ids.length || !bulkTargetStaffId) return;
    await applyBulkAssignment({
      fiscalClientIds: ids,
      action: "unassign",
      successMessage: "Asignación quitada en todas las empresas visibles.",
    });
  };

  const viewCounts = useMemo(
    () => countFiscalBoardByView(items, { scopedStaffUser }),
    [items, scopedStaffUser],
  );

  const effectiveFilterStaffId = useMemo(() => {
    if (boardView === FISCAL_BOARD_VIEW_POR_AUXILIAR) {
      return filterStaffId || bulkStaffId;
    }
    if (boardView === FISCAL_BOARD_VIEW_TODAS) {
      return bulkStaffId;
    }
    return filterStaffId;
  }, [boardView, filterStaffId, bulkStaffId]);

  const visibleRows = useMemo(
    () =>
      filterFiscalBoardItems(items, {
        view: boardView,
        searchQuery,
        filterStaffId: effectiveFilterStaffId,
        scopedStaffUser,
      }),
    [items, boardView, searchQuery, effectiveFilterStaffId, scopedStaffUser],
  );

  const showStaffSummary = isFiscalBoardStaffSummaryView(boardView, filterStaffId);

  const showBulkToolbar =
    canManageAssignments
    && boardView !== FISCAL_BOARD_VIEW_SUSPENDIDAS
    && !showStaffSummary;

  const bulkStaffSelectAllowsAll = showBulkToolbar;

  const staffSummary = useMemo(
    () => buildFiscalBoardStaffSummary(items, staffOptions, { searchQuery }),
    [items, staffOptions, searchQuery],
  );

  const bulkToolbarDisabled =
    boardView === FISCAL_BOARD_VIEW_SUSPENDIDAS ||
    fiscalBoardRowsEligibleForBulk(visibleRows).length === 0;

  const handleBoardExport = async (format) => {
    const key = `${boardView}-${format}`;
    setExportBusy(key);
    try {
      const exportStaffUserId = resolveFiscalBoardExportStaffId({
        boardView,
        filterStaffId,
        bulkStaffId,
      });
      await fiscalAssignmentBoardApi.exportBoard({
        format,
        view: boardView,
        staffUserId: exportStaffUserId,
        q: searchQuery,
      });
      toast.success(format === "pdf" ? "PDF descargado." : "Excel descargado.");
    } catch {
      toast.error("No pudimos exportar el tablero.");
    } finally {
      setExportBusy("");
    }
  };

  const onBoardViewChange = (nextView) => {
    setBoardView(nextView);
    if (nextView === FISCAL_BOARD_VIEW_POR_AUXILIAR) {
      setFilterStaffId(FISCAL_BOARD_STAFF_FILTER_ALL);
      setBulkStaffId(FISCAL_BOARD_STAFF_FILTER_ALL);
      persistBulkStaffId(FISCAL_BOARD_STAFF_FILTER_ALL);
    }
    if (nextView === FISCAL_BOARD_VIEW_TODAS || nextView === FISCAL_BOARD_VIEW_SIN_ASIGNAR) {
      setBulkStaffId(FISCAL_BOARD_STAFF_FILTER_ALL);
      persistBulkStaffId(FISCAL_BOARD_STAFF_FILTER_ALL);
    }
  };

  const handleFilterStaffChange = (value) => {
    setFilterStaffId(value);
    if (isFiscalBoardStaffFilterAll(value)) {
      setBulkStaffId(FISCAL_BOARD_STAFF_FILTER_ALL);
      persistBulkStaffId(value);
      return;
    }
    setBulkStaffId(value);
    persistBulkStaffId(value);
  };

  const handleBulkStaffChange = (value) => {
    setBulkStaffId(value);
    if (isFiscalBoardStaffFilterAll(value)) {
      setFilterStaffId(FISCAL_BOARD_STAFF_FILTER_ALL);
      persistBulkStaffId(value);
      return;
    }
    persistBulkStaffId(value);
    if (boardView === FISCAL_BOARD_VIEW_POR_AUXILIAR) {
      setFilterStaffId(value);
    }
  };

  const emptyMessage = scopedStaffUser ? DESPACHO_NO_ASSIGNED_CLIENTS_MESSAGE : EMPTY_FISCAL_BOARD_MESSAGE;

  const mapBoardRow = useCallback(
    (row) => ({
      ...row,
      id: row.fiscal_client_id,
      _tipo: tipoPersonaFiscalLabel(row.tipo_persona || row.contact_readiness?.tipo_persona),
      _assigned: formatAssignedUsersLabel(row.assigned_users),
      _contactos: fiscalContactSummaryLabel(row.contact_readiness),
    }),
    [],
  );

  const buildTableColumns = useCallback(
    (contextStaffId = null) => {
      const staffIdForCheckbox = contextStaffId || bulkTargetStaffId;
      const viewingAllStaff =
        !contextStaffId &&
        (isFiscalBoardStaffFilterAll(bulkStaffId) || !staffIdForCheckbox);

      const isAssignedToStaff = (row) => {
        const ids = assignedUserIds(row?.assigned_users);
        if (contextStaffId) {
          return ids.includes(contextStaffId);
        }
        if (viewingAllStaff) {
          return ids.length > 0;
        }
        if (!staffIdForCheckbox) return false;
        return ids.includes(staffIdForCheckbox);
      };

      const cols = [];

      if (canManageAssignments) {
        cols.push({
          key: "_bulk",
          label: "",
          render: (r) => {
            const suspended = isClientOperativoSuspendido(r);
            const saving = rowSavingId === r.fiscal_client_id;
            const rowAssignedIds = assignedUserIds(r?.assigned_users);
            const checked = isAssignedToStaff(r);
            const canToggleAssign =
              !suspended &&
              !saving &&
              !staffLoading &&
              (contextStaffId ||
                bulkTargetStaffId ||
                (viewingAllStaff && rowAssignedIds.length > 0));
            return (
              <Checkbox
                key={`bulk-${staffIdForCheckbox || "todos"}-${r.fiscal_client_id}`}
                checked={checked}
                disabled={
                  !canToggleAssign ||
                  (!contextStaffId && !viewingAllStaff && bulkToolbarDisabled)
                }
                onCheckedChange={(nextChecked) => {
                  const toggleStaffId =
                    contextStaffId ||
                    bulkTargetStaffId ||
                    (nextChecked === false ? rowAssignedIds[0] : undefined);
                  toggleRowBulkAssignment(r, nextChecked === true, toggleStaffId);
                }}
                aria-label={
                  suspended
                    ? `Empresa suspendida: ${r.razon_social || r.rfc}`
                    : checked
                      ? `Quitar asignación de ${r.razon_social || r.rfc}`
                      : `Asignar auxiliar seleccionado a ${r.razon_social || r.rfc}`
                }
                data-testid={`despacho-client-bulk-check-${r.fiscal_client_id}`}
              />
            );
          },
        });
      }

      cols.push(
        { key: "rfc", label: "RFC" },
        {
          key: "razon_social",
          label: "Nombre / razón social",
          render: (r) => (
            <div className="flex flex-col gap-1">
              <span>{r.razon_social || "—"}</span>
              <OperativoStatusBadge row={r} />
            </div>
          ),
        },
        { key: "_tipo", label: "Tipo" },
        { key: "_assigned", label: "Auxiliar responsable" },
        {
          key: "_contactos",
          label: "Contactos (Clientes)",
          render: (r) => <ContactReadinessBadge readiness={r.contact_readiness} />,
        },
      );

      if (canManageAssignments) {
        cols.push({
          key: "_actions",
          label: "Acciones",
          render: (r) => (
            <div className="flex flex-wrap gap-1 justify-end">
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="rounded-sm h-7 px-2"
                onClick={() => openAssign(r)}
                disabled={isClientOperativoSuspendido(r)}
                data-testid={`despacho-client-assign-${r.fiscal_client_id}`}
              >
                <Users className="h-3 w-3 mr-1" aria-hidden />
                Asignar
              </Button>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="rounded-sm h-7 px-2 text-[#0F2D5C]"
                asChild
              >
                <Link
                  to={buildClientesDeepLink({
                    clientId: r.fiscal_client_id,
                    tab: "credenciales",
                  })}
                  data-testid={`despacho-client-edit-fiscal-${r.fiscal_client_id}`}
                >
                  <ExternalLink className="h-3 w-3 mr-1" aria-hidden />
                  Contactos
                </Link>
              </Button>
            </div>
          ),
        });
      } else {
        cols.push({
          key: "_actions",
          label: "Contactos",
          render: (r) => (
            <Button type="button" variant="ghost" size="sm" className="rounded-sm h-7 px-2" asChild>
              <Link
                to={buildClientesDeepLink({
                  clientId: r.fiscal_client_id,
                  tab: "obligaciones",
                })}
                data-testid={`despacho-client-ver-clientes-${r.fiscal_client_id}`}
              >
                Ver en Clientes
              </Link>
            </Button>
          ),
        });
      }

      return cols;
    },
    [
      canManageAssignments,
      bulkTargetStaffId,
      bulkStaffId,
      rowSavingId,
      staffLoading,
      bulkToolbarDisabled,
      toggleRowBulkAssignment,
      openAssign,
    ],
  );

  const tableColumns = useMemo(() => buildTableColumns(), [buildTableColumns]);

  const tableRows = useMemo(() => visibleRows.map(mapBoardRow), [visibleRows, mapBoardRow]);

  return (
    <DespachoPageShell
      title="Asignacion de Empresas a Auxiliares"
      subtitle="Control interno del despacho: define qué auxiliar atiende cada empresa registrada en Clientes (catálogo fiscal)."
      testId="despacho-clientes"
    >
      <Accordion type="single" collapsible className="rounded-sm border border-slate-200 bg-slate-50">
        <AccordionItem value="informacion" className="border-0">
          <AccordionTrigger
            className="px-4 py-3 text-xs text-slate-700 hover:no-underline [&[data-state=open]]:pb-2"
            data-testid="despacho-clientes-domain-notice-trigger"
          >
            <span className="flex items-center gap-2 text-left">
              <Info className="w-4 h-4 shrink-0 text-slate-500" strokeWidth={1.75} aria-hidden />
              <span className="font-semibold text-[#0F2D5C]">Información</span>
            </span>
          </AccordionTrigger>
          <AccordionContent
            className="px-4 pb-3 pt-0 text-xs text-slate-600 leading-relaxed"
            data-testid="despacho-clientes-domain-notice"
          >
            <p className="m-0 pl-6" role="note">
              Registre primero las empresas en{" "}
              <Link to="/clientes" className="font-semibold text-[#0F2D5C] underline underline-offset-2">
                Clientes
              </Link>{" "}
              (RFC, nombre, WhatsApp y representantes). Aquí solo asigna{" "}
              <strong>usuarios auxiliares del comprador</strong> responsables. Los recordatorios futuros
              (honorarios, impuestos, cumpleaños) leerán los contactos del catálogo fiscal — sin duplicar
              datos en esta pantalla.
            </p>
          </AccordionContent>
        </AccordionItem>
      </Accordion>

      {readOnly ? (
        <div
          className="rounded-sm border border-sky-200 bg-sky-50 px-4 py-3 text-xs text-sky-950 flex flex-wrap items-center gap-2"
          data-testid="despacho-clientes-readonly-notice"
          role="status"
        >
          <Badge variant="secondary" className="rounded-sm bg-white text-sky-900 border-sky-200">
            <Eye className="h-3 w-3 mr-1" aria-hidden />
            {DESPACHO_READ_ONLY_BADGE}
          </Badge>
          <span>{DESPACHO_READ_ONLY_HELP}</span>
        </div>
      ) : null}

      {loadState === "loading" ? (
        <div className="flex items-center gap-2 text-sm text-slate-600 py-6" data-testid="despacho-clientes-loading">
          <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
          Cargando empresas del catálogo fiscal…
        </div>
      ) : null}

      {loadState === "forbidden" ? (
        <div
          className="rounded-sm border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-950"
          role="alert"
          data-testid="despacho-clientes-forbidden"
        >
          {loadMessage || FORBIDDEN_FISCAL_BOARD_MESSAGE}
        </div>
      ) : null}

      {loadState === "error" ? (
        <div className="space-y-3" data-testid="despacho-clientes-error">
          <div className="rounded-sm border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-950 flex items-start gap-2">
            <AlertCircle className="h-4 w-4 shrink-0 mt-0.5" aria-hidden />
            <p className="m-0">{loadMessage || LOAD_FISCAL_BOARD_ERROR}</p>
          </div>
          <Button type="button" variant="outline" size="sm" className="rounded-sm" onClick={loadBoard}>
            Reintentar
          </Button>
        </div>
      ) : null}

      {loadState === "empty" ? (
        <DespachoSectionCard
          title="Sin empresas en el catálogo fiscal"
          description={emptyMessage}
          data-testid={scopedStaffUser ? "despacho-clientes-scoped-empty" : "despacho-clientes-empty"}
        >
          <p className="text-sm text-slate-600 m-0">{emptyMessage}</p>
          {!scopedStaffUser ? (
            <Button type="button" className="mt-3 rounded-sm bg-[#0F2D5C] hover:bg-[#0a2248]" asChild>
              <Link to="/clientes">Ir a Clientes</Link>
            </Button>
          ) : null}
        </DespachoSectionCard>
      ) : null}

      {loadState === "ready" ? (
        <DespachoSectionCard
          title="Asignación por auxiliar"
          description={
            scopedStaffUser
              ? "Empresas asignadas a usted. Las suspendidas aparecen aparte y no admiten operación."
              : "Defina qué auxiliar atiende cada empresa. El alta y contactos fiscales se gestionan en Clientes."
          }
        >
          <Tabs value={boardView} onValueChange={onBoardViewChange} className="w-full">
            <TabsList
              className={`grid w-full h-auto gap-1 mb-4 ${
                scopedStaffUser
                  ? "max-w-md grid-cols-2"
                  : "max-w-3xl grid-cols-2 sm:grid-cols-4"
              }`}
              data-testid="despacho-fiscal-board-tabs"
            >
              {scopedStaffUser ? (
                <>
                  <TabsTrigger value={FISCAL_BOARD_VIEW_ACTIVAS} className="text-xs rounded-sm" data-testid="despacho-board-tab-activas">
                    Activas ({viewCounts.activas ?? 0})
                  </TabsTrigger>
                  <TabsTrigger value={FISCAL_BOARD_VIEW_SUSPENDIDAS} className="text-xs rounded-sm" data-testid="despacho-board-tab-suspendidas">
                    <PauseCircle className="w-3.5 h-3.5 mr-1.5" aria-hidden />
                    Suspendidas ({viewCounts.suspendidas ?? 0})
                  </TabsTrigger>
                </>
              ) : (
                <>
                  <TabsTrigger value={FISCAL_BOARD_VIEW_SIN_ASIGNAR} className="text-xs rounded-sm" data-testid="despacho-board-tab-sin-asignar">
                    Empresas por asignar ({viewCounts.sinAsignar ?? 0})
                  </TabsTrigger>
                  <TabsTrigger value={FISCAL_BOARD_VIEW_TODAS} className="text-xs rounded-sm" data-testid="despacho-board-tab-todas">
                    Todas ({viewCounts.todas ?? 0})
                  </TabsTrigger>
                  <TabsTrigger value={FISCAL_BOARD_VIEW_POR_AUXILIAR} className="text-xs rounded-sm" data-testid="despacho-board-tab-por-auxiliar">
                    Por auxiliar
                  </TabsTrigger>
                  <TabsTrigger value={FISCAL_BOARD_VIEW_SUSPENDIDAS} className="text-xs rounded-sm" data-testid="despacho-board-tab-suspendidas">
                    <PauseCircle className="w-3.5 h-3.5 mr-1.5" aria-hidden />
                    Suspendidas ({viewCounts.suspendidas ?? 0})
                  </TabsTrigger>
                </>
              )}
            </TabsList>

            <div className="flex flex-wrap items-center gap-3 mb-4">
              <div className="relative flex-1 min-w-[220px] max-w-md">
                <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" aria-hidden />
                <Input
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Buscar por RFC o razón social…"
                  className="pl-9 rounded-sm"
                  data-testid="despacho-fiscal-board-search"
                />
              </div>
              {!scopedStaffUser && boardView === FISCAL_BOARD_VIEW_POR_AUXILIAR ? (
                <div className="min-w-[220px]">
                  <Select
                    value={filterStaffId || FISCAL_BOARD_STAFF_FILTER_ALL}
                    onValueChange={handleFilterStaffChange}
                  >
                    <SelectTrigger className="rounded-sm bg-white" data-testid="despacho-board-filter-staff">
                      <SelectValue placeholder="Filtrar por auxiliar" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value={FISCAL_BOARD_STAFF_FILTER_ALL} data-testid="despacho-board-filter-all">
                        Todos los auxiliares
                      </SelectItem>
                      {staffOptions.map((s) => (
                        <SelectItem key={s.id} value={s.id}>
                          {s.name || s.email} · {despachoRoleLabel(s.despacho_role)}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              ) : null}
              <div className="flex flex-wrap gap-2">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="rounded-sm h-8"
                  disabled={!!exportBusy}
                  onClick={() => handleBoardExport("csv")}
                  data-testid="despacho-board-export-csv"
                >
                  <Download className="w-3.5 h-3.5 mr-1" aria-hidden />
                  {exportBusy.endsWith("-csv") ? "Exportando…" : "Excel"}
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="rounded-sm h-8"
                  disabled={!!exportBusy}
                  onClick={() => handleBoardExport("pdf")}
                  data-testid="despacho-board-export-pdf"
                >
                  <Download className="w-3.5 h-3.5 mr-1" aria-hidden />
                  {exportBusy.endsWith("-pdf") ? "Exportando…" : "PDF"}
                </Button>
              </div>
            </div>

            {showStaffSummary ? (
              <div
                className="rounded-sm border border-sky-200 bg-sky-50 px-3 py-2 text-xs text-sky-950 mb-4"
                role="status"
                data-testid="despacho-board-summary-notice"
              >
                Resumen por auxiliar. Elija un auxiliar en «Auxiliar contable» para asignar o quitar empresas.
              </div>
            ) : null}

            {boardView === FISCAL_BOARD_VIEW_SUSPENDIDAS ? (
              <div
                className="rounded-sm border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-950 mb-4"
                role="status"
                data-testid="despacho-board-suspended-notice"
              >
                Empresas suspendidas: visibles para consulta{scopedStaffUser ? " de su cartera" : ""}, sin asignación ni
                operación SAT hasta reanudarlas en Clientes.
              </div>
            ) : null}

            <TabsContent value={boardView} className="mt-0 space-y-4">
          {showBulkToolbar ? (
            <div className="space-y-2">
            <div
              className="flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-end sm:justify-between rounded-sm border border-slate-200 bg-slate-50/80 px-3 py-3"
              data-testid="despacho-clientes-bulk-toolbar"
            >
              <div className="space-y-1 min-w-[220px]">
                <label htmlFor="despacho-bulk-staff-select" className="text-xs font-medium text-slate-700">
                  Auxiliar contable
                </label>
                {staffLoading ? (
                  <p className="text-xs text-slate-500 m-0">Cargando auxiliares…</p>
                ) : staffOptions.length === 0 ? (
                  <p className="text-xs text-amber-800 m-0" data-testid="despacho-bulk-no-staff">
                    {DESPACHO_NO_STAFF_FOR_ASSIGNMENT_MESSAGE}
                  </p>
                ) : (
                  <Select
                    value={
                      bulkStaffId ||
                      (bulkStaffSelectAllowsAll
                        ? FISCAL_BOARD_STAFF_FILTER_ALL
                        : staffOptions[0]?.id ?? "")
                    }
                    onValueChange={handleBulkStaffChange}
                  >
                    <SelectTrigger
                      id="despacho-bulk-staff-select"
                      className="rounded-sm bg-white"
                      data-testid="despacho-bulk-staff-select"
                    >
                      <SelectValue placeholder="Seleccione auxiliar" />
                    </SelectTrigger>
                    <SelectContent>
                      {bulkStaffSelectAllowsAll ? (
                        <SelectItem
                          value={FISCAL_BOARD_STAFF_FILTER_ALL}
                          data-testid="despacho-bulk-staff-all"
                        >
                          Todos los auxiliares
                        </SelectItem>
                      ) : null}
                      {staffOptions.map((s) => (
                        <SelectItem key={s.id} value={s.id}>
                          {s.name || s.email} · {despachoRoleLabel(s.despacho_role)}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              </div>
              <div className="flex flex-wrap gap-2">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="rounded-sm"
                  disabled={
                    !bulkTargetStaffId ||
                    bulkSaving ||
                    staffLoading ||
                    staffOptions.length === 0 ||
                    bulkToolbarDisabled
                  }
                  onClick={handleSelectAllBulk}
                  data-testid="despacho-bulk-select-all-btn"
                >
                  Seleccionar todos
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="rounded-sm"
                  disabled={
                    !bulkTargetStaffId ||
                    bulkSaving ||
                    staffLoading ||
                    staffOptions.length === 0 ||
                    bulkToolbarDisabled
                  }
                  onClick={handleClearAllBulk}
                  data-testid="despacho-bulk-clear-all-btn"
                >
                  Quitar todos
                </Button>
              </div>
            </div>
            <p
              className="text-[10px] text-slate-500 m-0 px-1 leading-relaxed"
              data-testid="despacho-bulk-checkbox-hint"
            >
              La casilla indica si la empresa tiene auxiliar asignado (con «Todos los auxiliares») o si
              está asignada al auxiliar elegido arriba. La columna «Auxiliar responsable» muestra quién la
              atiende hoy; al marcar se guarda al instante y permanece hasta que la desmarque.
            </p>
            </div>
          ) : null}
          {showStaffSummary ? (
            <div className="space-y-4" data-testid="despacho-board-staff-summary">
              {staffSummary.sections.map((section) => {
                const rows = section.rows.map(mapBoardRow);
                return (
                  <div
                    key={section.staffId}
                    className="rounded-sm border border-slate-200 overflow-hidden"
                    data-testid={`despacho-board-staff-section-${section.staffId}`}
                  >
                    <div className="flex flex-wrap items-center justify-between gap-2 bg-slate-50 px-3 py-2 border-b border-slate-200">
                      <div>
                        <p className="text-sm font-semibold text-slate-900 m-0">{section.staffLabel}</p>
                        <p className="text-xs text-slate-500 m-0">
                          {despachoRoleLabel(section.staffRole)} · {rows.length}{" "}
                          {rows.length === 1 ? "empresa" : "empresas"}
                        </p>
                      </div>
                    </div>
                    {rows.length === 0 ? (
                      <p className="text-xs text-slate-500 m-0 px-3 py-3">Sin empresas asignadas.</p>
                    ) : (
                      <DespachoSimpleTable columns={buildTableColumns(section.staffId)} rows={rows} />
                    )}
                  </div>
                );
              })}
              <div
                className="rounded-sm border border-slate-200 overflow-hidden"
                data-testid="despacho-board-staff-section-unassigned"
              >
                <div className="flex flex-wrap items-center justify-between gap-2 bg-slate-50 px-3 py-2 border-b border-slate-200">
                  <div>
                    <p className="text-sm font-semibold text-slate-900 m-0">Empresas por asignar</p>
                    <p className="text-xs text-slate-500 m-0">
                      {staffSummary.unassignedRows.length}{" "}
                      {staffSummary.unassignedRows.length === 1 ? "empresa" : "empresas"} activas
                    </p>
                  </div>
                </div>
                {staffSummary.unassignedRows.length === 0 ? (
                  <p className="text-xs text-slate-500 m-0 px-3 py-3">Todas las empresas activas tienen auxiliar.</p>
                ) : (
                  <DespachoSimpleTable
                    columns={buildTableColumns()}
                    rows={staffSummary.unassignedRows.map(mapBoardRow)}
                  />
                )}
              </div>
            </div>
          ) : tableRows.length === 0 ? (
            <p className="text-sm text-slate-600 m-0 py-4" data-testid="despacho-board-filtered-empty">
              {boardView === FISCAL_BOARD_VIEW_SUSPENDIDAS
                ? "No hay empresas suspendidas en esta vista."
                : boardView === FISCAL_BOARD_VIEW_SIN_ASIGNAR
                  ? "No hay empresas pendientes de asignar."
                  : boardView === FISCAL_BOARD_VIEW_TODAS
                    ? "No hay empresas activas en el catálogo fiscal."
                    : searchQuery
                      ? "Ninguna empresa coincide con la búsqueda."
                      : "No hay empresas en esta vista."}
            </p>
          ) : (
            <DespachoSimpleTable columns={tableColumns} rows={tableRows} />
          )}
            </TabsContent>
          </Tabs>
        </DespachoSectionCard>
      ) : null}

      <Dialog open={assignOpen} onOpenChange={setAssignOpen}>
        <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto" data-testid="despacho-client-assign-dialog">
          <DialogHeader>
            <DialogTitle>Asignar auxiliar responsable</DialogTitle>
            <DialogDescription>
              {assignClient?.razon_social
                ? `Seleccione un solo auxiliar responsable de «${assignClient.razon_social}».`
                : "Seleccione un auxiliar interno activo del despacho."}
            </DialogDescription>
          </DialogHeader>
          {staffLoading ? (
            <p className="text-sm text-slate-600 m-0">Cargando usuarios del despacho…</p>
          ) : staffOptions.length === 0 ? (
            <p className="text-sm text-amber-800 m-0" data-testid="despacho-assign-no-staff">
              {DESPACHO_NO_STAFF_FOR_ASSIGNMENT_MESSAGE}
            </p>
          ) : (
            <div className="space-y-2 max-h-64 overflow-y-auto">
              {staffOptions.map((s) => (
                <label
                  key={s.id}
                  className="flex items-start gap-2 rounded-sm border border-slate-200 px-3 py-2 cursor-pointer hover:bg-slate-50"
                  data-testid={`despacho-assign-staff-${s.id}`}
                >
                  <Checkbox
                    checked={selectedStaffId === s.id}
                    onCheckedChange={() => selectStaffForAssign(s.id)}
                  />
                  <span className="text-sm">
                    <strong>{s.name || s.email}</strong>
                    <span className="block text-xs text-slate-500">
                      {despachoRoleLabel(s.despacho_role)} · {s.email}
                    </span>
                  </span>
                </label>
              ))}
            </div>
          )}
          {assignError ? (
            <p className="text-xs text-rose-700 m-0" role="alert">
              {assignError}
            </p>
          ) : null}
          <DialogFooter className="gap-2 sm:gap-0">
            <Button type="button" variant="outline" onClick={() => setAssignOpen(false)} disabled={assignSaving}>
              Cancelar
            </Button>
            <Button
              type="button"
              className="bg-[#0F2D5C] hover:bg-[#0a2248]"
              onClick={handleSaveAssignments}
              disabled={assignSaving || staffLoading || staffOptions.length === 0}
              data-testid="despacho-client-assign-save-btn"
            >
              {assignSaving ? "Guardando…" : "Guardar asignación"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </DespachoPageShell>
  );
}
